// Short cinematic flashes around major phase changes:
//   - Voting kicks off → gavel slam
//   - Night begins     → a curtain dropping over the screen
//   - Day breaks       → sunrise wash
// All variants are self-dismissing overlays keyed on a stable signature so
// they only play once per transition.

import { useEffect, useState } from 'react';
import type { Lang } from '../../../shared/src/types.js';

type Kind = 'vote-start' | 'night-fall' | 'day-break';

interface Props {
  t: Lang;
  kind: Kind;
  signature: string;
  durationMs?: number;
}

const COPY: Record<Kind, Record<Lang, { eyebrow: string; title: string }>> = {
  'vote-start': {
    ru: { eyebrow: 'СЛУШАНИЕ ОТКРЫТО', title: 'ГОЛОСОВАНИЕ' },
    uk: { eyebrow: 'СЛУХАННЯ ВІДКРИТО', title: 'ГОЛОСУВАННЯ' },
  },
  'night-fall': {
    ru: { eyebrow: 'ГОРОД ЗАСЫПАЕТ', title: 'НОЧЬ' },
    uk: { eyebrow: 'МІСТО ЗАСИНАЄ', title: 'НІЧ' },
  },
  'day-break': {
    ru: { eyebrow: 'ГОРОД ПРОСЫПАЕТСЯ', title: 'ДЕНЬ' },
    uk: { eyebrow: 'МІСТО ПРОКИДАЄТЬСЯ', title: 'ДЕНЬ' },
  },
};

export function PhaseFlash({ t, kind, signature, durationMs = 1300 }: Props) {
  const [playing, setPlaying] = useState(true);

  // Drive visibility off the signature directly. `signature` changes once per
  // transition, so this effect re-runs exactly when a replay is wanted. Each
  // run arms a fresh dismiss timer and its cleanup clears the stale one, which
  // makes React 18 StrictMode's double-invoke harmless (the old shownSig
  // early-return could leave the timer un-armed and the overlay lingering).
  useEffect(() => {
    setPlaying(true);
    const id = setTimeout(() => setPlaying(false), durationMs);
    return () => clearTimeout(id);
  }, [signature, durationMs]);

  if (!playing) return null;

  const copy = COPY[kind][t];

  return (
    <div className={`phase-flash pf-${kind}`} onClick={() => setPlaying(false)}>
      {kind === 'vote-start' && (
        <svg className="pf-gavel" viewBox="0 0 200 160">
          <g className="pf-gavel-head">
            <rect x="40" y="10" width="120" height="44" rx="4" fill="#a89c8b" stroke="#0a0a0c" strokeWidth="3" />
            <rect x="34" y="22" width="132" height="20" fill="#6e6457" stroke="#0a0a0c" strokeWidth="3" />
          </g>
          <line className="pf-gavel-shaft" x1="80" y1="44" x2="80" y2="120" stroke="#6e6457" strokeWidth="14" strokeLinecap="round" />
          <rect className="pf-gavel-block" x="20" y="130" width="160" height="22" fill="#3a3640" stroke="#0a0a0c" strokeWidth="3" />
        </svg>
      )}
      {kind === 'night-fall' && <div className="pf-curtain" />}
      {kind === 'day-break' && <div className="pf-sun" />}
      <div className="pf-copy">
        <div className="pf-eyebrow mono">{copy.eyebrow}</div>
        <div className="pf-title display">{copy.title}</div>
      </div>
    </div>
  );
}
